import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { DIALOG_TYPE } from '../../../../constants/DialogTypes';
import { Dialog } from '../../../../ui/components/Common/Dialog/Dialog';
import { CandiBankFolder } from '../../../../utils/types/candi_bank_types';

interface AddToFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  folders: CandiBankFolder[];
  candidateIds: string[];
  /** Single-candidate mode shows the name instead of a count. */
  candidateName?: string;
  onAdd: (folder: CandiBankFolder, candidateIds: string[]) => Promise<void>;
}

/** The non-drag way to file candidates: pick a folder from a list. */
export const AddToFolderModal: React.FC<AddToFolderModalProps> = ({
  isOpen,
  onClose,
  folders,
  candidateIds,
  candidateName,
  onAdd,
}) => {
  const [selected, setSelected] = useState<{ value: string; label: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) setSelected(null);
  }, [isOpen]);

  const options = useMemo(
    () => folders.map((f) => ({ value: f.id, label: `${f.name} — ${f.createdBy.name}` })),
    [folders],
  );

  const handleConfirm = async () => {
    const folder = folders.find((f) => f.id === selected?.value);
    if (!folder || candidateIds.length === 0) return;
    setSubmitting(true);
    try {
      await onAdd(folder, candidateIds);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const count = candidateIds.length;
  const who = candidateName ?? `${count} candidate${count !== 1 ? 's' : ''}`;

  return (
    <Dialog
        isOpen={isOpen}
        onClose={onClose}
        handleApply={handleConfirm}
        title='Add to Folder'
        confirmButtonLable={submitting ? 'Adding...' : 'Add to Folder'}
        cancelButtonLable='Cancel'
        dialogType={DIALOG_TYPE.SUCCESS}
        maxWidth='440px'
        disabled={!selected || submitting}
    >
      <div className='flex flex-col gap-4 py-1'>
        <p className='text-sm text-slate-500'>
          Add <span className='font-bold text-slate-800'>{ who }</span> to a folder.
        </p>
        <Select
            options={options}
            value={selected}
            onChange={(opt: any) => setSelected(opt)}
            placeholder={folders.length ? 'Choose a folder...' : 'No folders yet — create one first'}
            isDisabled={folders.length === 0}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 10050 }) }}
        />
      </div>
    </Dialog>
  );
};
